import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Input } from '@/shared/components/Input';
import { Textarea } from '@/shared/components/Textarea';
import { Button } from '@/shared/components/Button';
import { ApiError } from '@/lib/api-client';
import { StudentSearchBar } from '@/features/director-students/components/StudentSearchBar';
import { useCreateApprovedStudent, useUpdateApprovedStudent } from '../hooks/useApprovedStudents';
/** Builds the cached, privacy-safe surname initial (e.g. "Pérez" → "P."). */
function surnameInitial(lastName) {
    const trimmed = lastName.trim();
    return trimmed ? `${trimmed.charAt(0).toUpperCase()}.` : '';
}
export function ApprovedStudentForm({ onSuccess, defaultValues, id }) {
    const [serverError, setServerError] = useState('');
    const isEdit = !!id;
    const createMutation = useCreateApprovedStudent();
    const updateMutation = useUpdateApprovedStudent(id ?? '');
    const isPending = createMutation.isPending || updateMutation.isPending;
    const { register, handleSubmit, setValue, watch, formState: { errors } } = useForm({
        defaultValues: {
            is_visible: true,
            passed_date: '',
            student_id: '',
            opposition_id: '',
            first_name: '',
            last_name_initial: '',
            opposition_name: '',
            ...defaultValues,
        },
    });
    const studentId = watch('student_id');
    const firstName = watch('first_name');
    const lastNameInitial = watch('last_name_initial');
    const oppositionName = watch('opposition_name');
    function handlePickStudent(r) {
        setValue('student_id', r.id);
        setValue('first_name', r.first_name);
        setValue('last_name_initial', surnameInitial(r.last_name));
        setValue('opposition_id', r.opposition_id);
        setValue('opposition_name', r.opposition_name);
    }
    function onSubmit(values) {
        setServerError('');
        if (!isEdit && !values.student_id) {
            setServerError('Selecciona el alumno que ha aprobado.');
            return;
        }
        const payload = {
            ...values,
            destination: values.destination?.trim() || undefined,
            photo_url: values.photo_url?.trim() || undefined,
            testimonial: values.testimonial?.trim() || undefined,
        };
        const handler = isEdit ? updateMutation : createMutation;
        handler.mutate(payload, {
            onSuccess: () => onSuccess(),
            onError: (err) => {
                setServerError(err instanceof ApiError ? err.message : 'Ha ocurrido un error.');
            },
        });
    }
    return (_jsxs("form", { onSubmit: handleSubmit(onSubmit), className: "flex flex-col gap-4", noValidate: true, children: [!isEdit && (_jsxs("div", { children: [_jsx("label", { className: "mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300", children: "Alumno aprobado" }), studentId ? (_jsxs("div", { className: "flex items-center justify-between rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-gray-700", children: [_jsxs("span", { className: "text-gray-900 dark:text-gray-100", children: [firstName, " ", lastNameInitial, " \u2014 ", oppositionName] }), _jsx(Button, { type: "button", variant: "ghost", size: "sm", onClick: () => setValue('student_id', ''), children: "Cambiar" })] })) : (_jsx(StudentSearchBar, { onSelect: handlePickStudent, placeholder: "Buscar al alumno por n\u00BA, nombre, DNI o email\u2026" }))] })), _jsxs("div", { className: "grid gap-4 sm:grid-cols-2", children: [_jsx(Input, { label: "Nombre", error: errors.first_name?.message, ...register('first_name', { required: 'El nombre es obligatorio' }) }), _jsx(Input, { label: "Inicial del apellido", hint: "Solo la inicial (ej. P.)", error: errors.last_name_initial?.message, ...register('last_name_initial', { required: 'La inicial es obligatoria' }) })] }), _jsx(Input, { label: "Oposici\u00F3n (texto para la landing)", error: errors.opposition_name?.message, ...register('opposition_name', { required: 'La oposición es obligatoria' }) }), _jsxs("div", { className: "grid gap-4 sm:grid-cols-2", children: [_jsx(Input, { label: "Fecha de aprobaci\u00F3n", type: "date", error: errors.passed_date?.message, ...register('passed_date', { required: 'La fecha es obligatoria' }) }), _jsx(Input, { label: "Destino / plaza (opcional)", ...register('destination') })] }), _jsx(Input, { label: "URL de foto (opcional)", type: "url", ...register('photo_url') }), _jsx(Textarea, { label: "Testimonio (opcional)", rows: 3, ...register('testimonial') }), _jsxs("label", { className: "flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300", children: [_jsx("input", { type: "checkbox", className: "h-4 w-4 rounded border-gray-300", ...register('is_visible') }), "Visible en la landing"] }), serverError && (_jsx("p", { role: "alert", className: "text-sm text-red-600 dark:text-red-400", children: serverError })), _jsx(Button, { type: "submit", loading: isPending, className: "w-full", children: isEdit ? 'Guardar cambios' : 'Registrar aprobado' })] }));
}
//# sourceMappingURL=ApprovedStudentForm.js.map